import type { FeatureKey } from '@palacete/shared/plans';
import type { CollectionTypeDef } from './types';
import { pickLoc } from './collections';

export type PanelNavItem = {
  id: string;
  label: string;
  description: string;
  to: string;
  area: 'core' | 'operations' | 'support';
  mobileTab?: boolean;
  /** BIZ-07A: feature de plan que gatea este item. Sin ella y por madurez, el
   *  item no existe en la UI; sin ella y solo por plan, se muestra con candado. */
  feature?: FeatureKey;
};

export const toolsNav = { id: 'tools', to: '/tools', label: 'Herramientas e integraciones', description: 'Añade, configura y prueba herramientas para tu sitio.', area: 'operations' as const };

export const coreNavItems: PanelNavItem[] = [
  toolsNav,
  {
    id: 'dashboard',
    label: 'Inicio',
    description: 'Resumen de actividad, métricas y accesos rápidos.',
    to: '/',
    area: 'core',
    mobileTab: true,
  },
  {
    id: 'site',
    label: 'Sitio',
    description: 'Páginas, apariencia, contacto y publicación.',
    to: '/site',
    area: 'core',
    mobileTab: true,
  },
  {
    id: 'bookings',
    label: 'Reservas',
    description: 'Agenda, disponibilidad y reglas de atención.',
    to: '/bookings',
    area: 'operations',
    feature: 'bookings',
  },
  {
    id: 'reputation',
    label: 'Mis reseñas',
    description: 'Solicitudes, respuestas y prueba social de tus clientes.',
    to: '/reputation',
    area: 'operations',
  },
  {
    id: 'surveys',
    label: 'Encuestas y campañas',
    description: 'Capta interés desde redes, segmenta y entrega tu material a cambio del contacto.',
    to: '/surveys',
    area: 'operations',
  },
  {
    id: 'visibility',
    label: 'Resumen y visibilidad',
    description: 'Tu diagnóstico de presencia online y los pasos que más mueven la aguja.',
    to: '/visibility',
    area: 'operations',
  },
  {
    id: 'chatbot',
    label: 'Asistente',
    description: 'Configuración y uso del asistente del sitio.',
    to: '/chatbot',
    area: 'core',
    feature: 'chatbot',
  },
  {
    id: 'messages',
    label: 'Mensajes',
    description: 'Lo que te escribieron desde el formulario de contacto de tu sitio.',
    to: '/submissions',
    area: 'support',
  },
  {
    id: 'contacts',
    label: 'Clientes',
    description: 'Personas que han levantado la mano desde tu sitio.',
    to: '/contacts',
    area: 'support',
    mobileTab: true,
  },
  {
    id: 'media',
    label: 'Archivos e imágenes',
    description: 'Tus imágenes, documentos y códigos QR para compartir.',
    to: '/media',
    area: 'support',
  },
  {
    id: 'qr',
    label: 'Códigos QR',
    description: 'Genera y descarga QR para compartir páginas o campañas.',
    to: '/qr',
    area: 'support',
  },
  {
    id: 'seo',
    label: 'SEO y visibilidad',
    description: 'Metadatos y presencia en buscadores.',
    to: '/seo',
    area: 'support',
  },
  {
    id: 'settings',
    label: 'Ajustes',
    description: 'Configuración general, usuarios y dominio.',
    to: '/settings',
    area: 'support',
  },
  {
    id: 'help',
    label: 'Ayuda',
    description: 'Preguntas frecuentes, asistente y contacto con soporte.',
    to: '/ayuda',
    area: 'support',
  },
  {
    id: 'billing',
    label: 'Mi pago',
    description: 'Estado de tu suscripción, recibos y gestión de tu plan.',
    to: '/billing',
    area: 'support',
  },
];

export function collectionNavItems(types: CollectionTypeDef[]): PanelNavItem[] {
  return [...types]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((type) => ({
      id: `collection:${type.key}`,
      label: pickLoc(type.labels.plural, 'es') || type.key,
      description: `Gestiona ${pickLoc(type.labels.plural, 'es') || type.key}.`,
      to: `/c/${type.key}`,
      area: 'core' as const,
    }));
}

export function dashboardQuickActions(types: CollectionTypeDef[]): PanelNavItem[] {
  return [
    coreNavItems.find((item) => item.id === 'site'),
    ...collectionNavItems(types).slice(0, 2),
    coreNavItems.find((item) => item.id === 'messages'),
  ].filter((item): item is PanelNavItem => Boolean(item));
}

export const platformPluginsNav = { to: '/platform/plugins', label: 'Catálogo de herramientas', description: 'Controla quién puede usar cada herramienta y su versión.' };
